import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, ArrowRight, X } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { getFamilyDefinition } from '../content';
interface ChallengeAcceptModalProps {
  isOpen: boolean;
  familyId: string;
  masteryScore: number;
  onAccept: () => void;
  onDecline: () => void;
}
/**
 * ChallengeAcceptModal — Commitment modal before starting a challenge session.
 *
 * Shows the student their current mastery level and the challenge tier they'll face.
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function ChallengeAcceptModal({
  isOpen,
  familyId,
  masteryScore,
  onAccept,
  onDecline
}: ChallengeAcceptModalProps) {
  const family = getFamilyDefinition(familyId);
  if (!isOpen || !family) return null;
  // Map mastery score to the 16-tier system: mastery 70% ≈ tier 11, 80% ≈ tier 13
  const currentTier = Math.min(16, Math.max(1, Math.round(masteryScore / 100 * 16)));
  const challengeTier = Math.min(16, currentTier + 2);
  // Map challengeTier (1-16) to 1-5 stars
  const getStars = (tier: number) => {
    if (tier <= 3) return '⭐';
    if (tier <= 6) return '⭐⭐';
    if (tier <= 9) return '⭐⭐⭐';
    if (tier <= 12) return '⭐⭐⭐⭐';
    return '⭐⭐⭐⭐⭐';
  };
  return <AnimatePresence data-id="element-2148">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50" onClick={onDecline} data-id="element-2149" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 24
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.9,
        y: 24
      }} transition={{
        type: 'spring',
        bounce: 0.3,
        duration: 0.45
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-sm md:w-full z-50 flex items-center" data-id="element-2150">
            <Card className="w-full overflow-hidden shadow-2xl border-2 border-violet-100 rounded-3xl" data-id="element-2151">
              {/* Header gradient */}
              <div className="bg-gradient-to-br from-violet-500 to-fuchsia-600 p-6 text-white relative overflow-hidden" data-id="element-2152">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" data-id="element-2153" />
                <button onClick={onDecline} className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors" aria-label="Sluiten" data-id="element-2154">
                  <X className="w-4 h-4 text-white" data-id="element-2155" />
                </button>

                <motion.div initial={{
              scale: 0,
              rotate: -15
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              type: 'spring',
              bounce: 0.5,
              delay: 0.15
            }} className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4" data-id="element-2156">
                  <Trophy className="w-9 h-9 text-white" strokeWidth={2.5} data-id="element-2157" />
                </motion.div>

                <h2 className="text-xl font-black leading-tight mb-1" data-id="element-2158">
                  Klaar voor de uitdaging?
                </h2>
                <p className="text-white/80 text-sm font-medium" data-id="element-2159">
                  {family.description}
                </p>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4" data-id="element-2160">
                {/* Stats */}
                <div className="grid grid-cols-2 gap-3" data-id="element-2161">
                  <div className="bg-slate-50 rounded-2xl p-3 text-center border border-slate-100" data-id="element-2162">
                    <div className="text-2xl font-black text-slate-700" data-id="element-2163">
                      {Math.round(masteryScore)}%
                    </div>
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mt-0.5" data-id="element-2164">
                      Jouw niveau
                    </div>
                  </div>
                  <div className="bg-violet-50 rounded-2xl p-3 text-center border border-violet-100" data-id="element-2165">
                    <div className="text-2xl font-black text-violet-700" data-id="element-2166">
                      {getStars(challengeTier)}
                    </div>
                    <div className="text-xs font-bold text-violet-400 uppercase tracking-wide mt-0.5" data-id="element-2167">
                      Hoe moeilijk?
                    </div>
                  </div>
                </div>

                {/* Message */}
                <p className="text-sm text-slate-600 font-medium text-center leading-relaxed" data-id="element-2168">
                  Dit wordt moeilijker dan je gewend bent — maar jij kunt het!
                  💪
                </p>

                {/* Actions */}
                <div className="space-y-2 pt-1" data-id="element-2169">
                  <Button onClick={onAccept} variant="primary" size="lg" fullWidth className="bg-gradient-to-r from-violet-500 to-fuchsia-600 border-none shadow-lg shadow-violet-200 rounded-2xl font-black" data-id="element-2170">
                    Start Uitdaging <ArrowRight className="w-4 h-4 ml-2" data-id="element-2171" />
                  </Button>
                  <Button onClick={onDecline} variant="secondary" size="lg" fullWidth className="rounded-2xl" data-id="element-2172">
                    Toch niet
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </>}
    </AnimatePresence>;
}